import i18n from '@/utils/i18n';
import React from 'react';
import { BusinessHours, formatHoursRange } from '@/utils/business-hours';
import { createStyles } from '@/contexts/theme-context';
import { Icons, Typography, XStack } from '@usepepper/sprout-kit-ui';
import { Pressable } from 'react-native';

interface AccountDetailHoursPillProps {
  isCurrentlyOpen: boolean;
  isExpanded: boolean;
  nextOpenSlot: BusinessHours | null;
  onPress: () => void;
}

export const AccountDetailHoursPill: React.FC<AccountDetailHoursPillProps> = ({
  isCurrentlyOpen,
  isExpanded,
  nextOpenSlot,
  onPress,
}) => {
  const [styles, theme] = useStyles();

  const accessibilityLabel = [
    i18n.t('sales_portal.account_detail.hours.title'),
    isCurrentlyOpen ? i18n.t('open') : i18n.t('closed'),
    nextOpenSlot ? formatHoursRange(nextOpenSlot, ' — ') : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ expanded: isExpanded }}
      onPress={onPress}
      style={styles.pill}
    >
      <Typography
        color={isCurrentlyOpen ? '$textSuccess' : '$textSecondary'}
        size="$md"
        variant="label"
      >
        {isCurrentlyOpen ? i18n.t('open') : i18n.t('closed')}
      </Typography>
      {nextOpenSlot ? (
        <Typography color="$textSecondary" size="$md" variant="body">
          {formatHoursRange(nextOpenSlot, ' — ')}
        </Typography>
      ) : null}
      <XStack
        alignItems="center"
        justifyContent="center"
        rotate={isExpanded ? '180deg' : '0deg'}
      >
        <Icons.ChevronDown color={theme.sk.color.icon.secondary} size="$16" />
      </XStack>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  pill: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusRound,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    marginTop: theme.sk.spacing.spaceXsmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
}));
